import { Alert, Anchor, Badge, Box, Button, Card, Checkbox, Divider, Grid, Group, Input, Radio, Select, Stack, TagsInput, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  CLIENT_REQUEST_TYPE_LABELS,
  CLIENT_REQUEST_TYPES,
  REFERENCE_REQUIRED_TYPES,
  SAMPLE_PACK_LABELS,
  SAMPLE_PACK_TYPES,
  SAMPLE_STATUS_LABELS,
  SAMPLE_STATUSES,
  type ClientRequestDetailDto,
  type ClientRequestType,
  type FileRefDto,
  type SamplePackType,
} from '@mexp/shared';
import { errorMessage, http } from '@/api/client';
import { FileUploader } from '@/features/files/FileUploader';
import { notifySuccess } from '@/lib/notify';
import { portalKeys, useClientOptions, useClientRequest } from './api';
import { BackButton, usePortalBack } from './BackButton';
import { stageLabel, STAGE_COLOR, STAGE_HELP, STAGE_ORDER } from './stage';

const TYPE_HELP: Partial<Record<ClientRequestType, string>> = {
  // dossier: 'Makin prepares the dossier checklist for the product and destination.',
  // guidelines: 'Makin shares what the destination’s registration asks for.',
  // documents: 'Choose the documents the registration asks for.',
};

const PACK_HELP: Partial<Record<SamplePackType, string>> = {
  plain: 'Bulk product will be packed in plain packing material.',
  commercial: 'Bulk product will be packed in primary and secondary packing material with approved artwork',
};

const referenceRequired = (type: ClientRequestType | null) => !!type && REFERENCE_REQUIRED_TYPES.includes(type);

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Stack gap="md">
      <Stack gap={2}>
        <Title order={4}>{title}</Title>
        {hint && (
          <Text fz="sm" c="dimmed">
            {hint}
          </Text>
        )}
      </Stack>
      {children}
    </Stack>
  );
}

/** A dossier requisition: what the client needs, for which product and country. Makin gives the date later. */
export function ClientNewRequestPage() {
  const navigate = useNavigate();
  const back = usePortalBack('/requisitions');
  const queryClient = useQueryClient();
  const options = useClientOptions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      requestType: null as ClientRequestType | null,
      documents: [] as string[],
      genericName: '',
      brandName: '',
      country: null as string | null,
      category: null as string | null,
      dosageForm: null as string | null,
      packSize: '',
      similarProduct: '',
      sample: false,
      packType: null as SamplePackType | null,
      referenceFiles: [] as FileRefDto[],
    },
    validate: {
      requestType: (v) => (v ? null : 'Choose what you need'),
      documents: (v, values) => (values.requestType === 'documents' && !v.length ? 'Choose at least one document' : null),
      genericName: (v) => (v.trim() ? null : 'Enter the generic name'),
      country: (v) => (v ? null : 'Choose the destination country'),
      packType: (v, values) => (values.sample && !v ? 'Choose a plain or a commercial sample' : null),
      referenceFiles: (v, values) => (referenceRequired(values.requestType) && !v.length ? 'Attach at least one reference file' : null),
    },
  });
  const type = form.values.requestType;
  const needsReference = referenceRequired(type);

  // "New requisition for this product" from a listing: start from that dossier's product details.
  const [params] = useSearchParams();
  const source = useClientRequest(params.get('from') ?? '');
  useEffect(() => {
    const r = source.data;
    if (!r || form.isDirty()) return;
    // Requisitions from before the names were asked only have the product's name: it starts as the brand name.
    form.setValues({
      genericName: r.genericName ?? '',
      brandName: r.brandName ?? (r.genericName ? '' : r.productName),
      category: r.category,
      dosageForm: r.dosageForm,
      packSize: r.packSize ?? '',
    });
    form.resetDirty();
    // Fill once, when the source dossier arrives; later edits are the client's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.data]);

  const create = useMutation({
    mutationFn: (values: typeof form.values) =>
      http.post<ClientRequestDetailDto>('/portal/requests', {
        requestType: values.requestType,
        genericName: values.genericName.trim(),
        brandName: values.brandName.trim() || undefined,
        country: values.country,
        category: values.category ?? undefined,
        dosageForm: values.dosageForm ?? undefined,
        packSize: values.packSize.trim() || undefined,
        similarProduct: values.similarProduct.trim() || undefined,
        requiredDocs: values.requestType === 'documents' ? values.documents.map((name) => ({ name })) : [],
        referenceFileIds: values.referenceFiles.map((f) => f.id),
        sample: values.sample && values.packType ? { packType: values.packType } : undefined,
      }),
    onSuccess: async (request) => {
      await queryClient.invalidateQueries({ queryKey: portalKeys.all });
      notifySuccess(`Requisition ${request.code} sent to Makin.`);
      navigate(`/requests/${request.id}`, { replace: true });
    },
    onError: (e) => {
      setError(errorMessage(e));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  return (
    <Stack gap="md">
      <Stack gap="md">
        <BackButton fallback="/requisitions" />
        <Stack gap={6}>
          <Title order={1} fz={{ base: 26, sm: 32 }} style={{ letterSpacing: '-0.015em' }}>
            New Requisition
          </Title>
          {/* <Text c="dimmed" maw={620}>
            Tell Makin what the registration needs. The regulatory team plans the work, gives you a date, and you can follow the dossier from your dashboard.
          </Text> */}
          {source.data && (
            <Text fz="sm" maw={620}>
              Same product as{' '}
              <Anchor component={Link} to={`/requests/${source.data.id}`} fz="sm" className="tabular">
                {source.data.code}
              </Anchor>{' '}
              ({source.data.productName} for {source.data.country}): its details are filled in. Choose the destination and what you need this time.
            </Text>
          )}
        </Stack>
      </Stack>

      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Card component="form" noValidate onSubmit={form.onSubmit((values) => create.mutate(values))} p={{ base: 'md', sm: 32 }}>
            <Stack gap={28}>
              {error && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} title="The requisition was not sent">
                  {error}
                </Alert>
              )}

              <Section title="Details of Product & Registration Country">
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput label="Generic name" placeholder="e.g. Persea extract" withAsterisk {...form.getInputProps('genericName')} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput label="Brand name" placeholder="Optional" {...form.getInputProps('brandName')} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="Destination country"
                      placeholder="Choose a country"
                      data={options.data?.countries ?? []}
                      searchable
                      withAsterisk
                      nothingFoundMessage="Not in the list? Ask your Makin account manager to add it."
                      {...form.getInputProps('country')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select label="Category" placeholder="Optional" data={options.data?.categories ?? []} clearable {...form.getInputProps('category')} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <Select
                      label="Dosage form"
                      placeholder="Optional"
                      data={options.data?.dosageForms ?? []}
                      clearable
                      searchable
                      {...form.getInputProps('dosageForm')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 4 }}>
                    <TextInput label="Pack size" placeholder="e.g. 3 × 10 capsules" {...form.getInputProps('packSize')} />
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <TextInput
                      label="Existing similar product"
                      placeholder="Optional · a product of yours like this one, e.g. one already registered"
                      {...form.getInputProps('similarProduct')}
                    />
                  </Grid.Col>
                </Grid>
              </Section>

              <Divider />

              <Section title="Requisition Details">
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, sm: 5 }}>
                    <Stack gap={6}>
                      <Select
                        label="Requisition type"
                        placeholder="Choose one"
                        data={CLIENT_REQUEST_TYPES.map((t) => ({ value: t, label: CLIENT_REQUEST_TYPE_LABELS[t] }))}
                        allowDeselect={false}
                        withAsterisk
                        {...form.getInputProps('requestType')}
                      />
                      {type && TYPE_HELP[type] && (
                        <Text fz="xs" c="dimmed">
                          {TYPE_HELP[type]}
                        </Text>
                      )}
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 7 }}>
                    <Input.Wrapper
                      label="Reference files"
                      // description="Your country checklist, a required format, or an earlier certificate."
                      withAsterisk={needsReference}
                      // A switch to specific documents drops the requirement, and with it any error already shown.
                      error={needsReference ? form.errors.referenceFiles : undefined}
                    >
                      <Box mt={6}>
                        <FileUploader
                          area="dr_reference"
                          value={form.values.referenceFiles}
                          onChange={(files) => form.setFieldValue('referenceFiles', files)}
                          onBusyChange={setBusy}
                        />
                      </Box>
                    </Input.Wrapper>
                  </Grid.Col>
                </Grid>
                {type === 'documents' && (
                  <TagsInput
                    label="Documents"
                    // description="Type a document and press Enter. Add as many as you need."
                    placeholder={form.values.documents.length ? undefined : 'e.g. Finished Product COA'}
                    withAsterisk
                    clearable
                    acceptValueOnBlur
                    {...form.getInputProps('documents')}
                  />
                )}
              </Section>

              <Divider />

              <Section title="Container Closure System">
                <Checkbox
                  label="Send me a sample"
                  checked={form.values.sample}
                  onChange={(e) => {
                    form.setFieldValue('sample', e.currentTarget.checked);
                    if (!e.currentTarget.checked) form.setFieldValue('packType', null);
                  }}
                />
                {form.values.sample && (
                  <Radio.Group label="Packaging" withAsterisk {...form.getInputProps('packType')}>
                    <Stack gap="sm" mt={8}>
                      {SAMPLE_PACK_TYPES.map((p) => (
                        <Radio key={p} value={p} label={SAMPLE_PACK_LABELS[p]} description={PACK_HELP[p]} />
                      ))}
                    </Stack>
                  </Radio.Group>
                )}
              </Section>


              <Group justify="flex-end" gap="sm">
                <Button variant="default" component={Link} to={back.to} onClick={back.onClick}>
                  Cancel
                </Button>
                <Button type="submit" size="md" loading={create.isPending} disabled={busy}>
                  {busy ? 'Waiting for uploads…' : 'Send requisition'}
                </Button>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        {/* <Grid.Col span={{ base: 12, lg: 4 }}>
          <Box pos="sticky" top={88}>
            <Stack gap="md">
              <Title order={4}>What happens next</Title>
              <Stack gap={14}>
                {STAGE_ORDER.map((s, i) => (
                  <Group key={s} gap="sm" wrap="nowrap" align="flex-start">
                    <Badge circle size="lg" variant="light" color={STAGE_COLOR[s]} style={{ flex: 'none' }}>
                      {i + 1}
                    </Badge>
                    <Stack gap={2}>
                      <Text fz="sm" fw={600}>
                        {stageLabel(s)}
                      </Text>
                      <Text fz="sm" c="dimmed" lh={1.5}>
                        {STAGE_HELP[s as keyof typeof STAGE_HELP]}
                      </Text>
                    </Stack>
                  </Group>
                ))}
              </Stack>
              {form.values.sample && (
                <Text fz="sm" c="dimmed" lh={1.5}>
                  Your sample shows on the dossier as {SAMPLE_STATUSES.map((st) => SAMPLE_STATUS_LABELS[st]).join(', then ')}, with the courier and tracking
                  number once it is on its way.
                </Text>
              )}
            </Stack>
          </Box>
        </Grid.Col> */}
      </Grid>
    </Stack>
  );
}
